import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Divider } from '@mui/material';
import { useSnackbar } from "notistack";

import { useAppDispatch } from '../../state/hooks';
import { loginUser, SignInResult } from '../../state/actions/authActions';
import { RootState } from '../../state/store';
import { setCurrentView } from '../../state/reducers/viewReducer';
import { Button } from '../../components/Buttons/Button';
import SsoButtons from './SsoButtons';
import { useSsoProviders } from './useSsoProviders';

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const rehydrated = useSelector((s: RootState) => s?._persist?.rehydrated === true);
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [failure, setFailure] = useState<{ message: string; noAccount?: boolean } | null>(null);
  const ssoProviders = useSsoProviders();
  const hasSso = !!(ssoProviders.google || ssoProviders.microsoft || ssoProviders.linkedIn);
  const signupUrl = ssoProviders.signup?.url;

  useEffect(() => {
    if (rehydrated && currentView !== "Login") {
      dispatch(setCurrentView("Login"));
    }
  }, [currentView, dispatch, rehydrated]);

  // Note: the card is always white
  useEffect(() => {
    document.documentElement.dataset.theme = "light";
  }, []);

  const handleLogin = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    setFailure(null);
    if (isWorking) return;
    if (!email || !password) {
      setFailure({ message: 'Please enter your email and password.' });
      return;
    }

    setIsWorking(true);
    showResult(await dispatch(loginUser(email, password)));
  };

  const showResult = (result: SignInResult) => {
    if (result.ok) {
      enqueueSnackbar('Login successful.', { variant: 'success', autoHideDuration: 3000 });
    } else {
      setFailure({ message: result.error, noAccount: result.noAccount });
    }
  };

  const handleSsoResult = (result: SignInResult) => {
    setIsWorking(false);
    showResult(result);
  };

  return (
    <div className="w-full min-h-screen flex flex-col justify-center gap-6 md:gap-10"
      style={{ backgroundImage: 'url("eclipse-miningware__hero-img.webp")', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
    >
      <div className="w-auto px-4 text-center fade-up">
        <div className="inline-block glow-radial">
          <img
            src="eclipse-miningware-logo.webp"
            alt="Logo"
            width={200}
            height={74}
            className="mx-auto w-[15rem] sm:w-[20rem] h-auto"
          />
        </div>
      </div>
      <div className="flex items-center justify-center fade-up">
        <div className="bg-white px-8 pb-8 pt-12 shadow-[0_2px_30px_20px_rgba(255,255,255,0.3),0_3px_6px_rgba(0,0,0,0.23)] w-full min-h-48 w-full flex flex-col justify-center lg:max-w-lg lg:pb-10 lg:px-10 lg:pt-14 lg:rounded-lg lg:min-w-[40rem]">
          <div className="flex flex-col items-center">
            <h2 className="text-4xl font-semibold text-center text-theme-primary">Sign In</h2>
            <Divider className="!my-8 w-full max-w-16 !border-theme-border" />
          </div>
          {hasSso && (
            <>
              <SsoButtons
                providers={ssoProviders}
                disabled={isWorking}
                onStart={() => { setFailure(null); setIsWorking(true); }}
                onResult={handleSsoResult}
              />
              <Divider className="!my-6 !border-theme-border">
                <span className="text-theme-text-muted text-sm px-2">or</span>
              </Divider>
            </>
          )}
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              setIsWorking(true);
              await handleLogin(e);
              setIsWorking(false);
            }}
          >
            <div>
              <input
                type="text"
                name="email"
                placeholder="Email"
                value={email}
                autoComplete="email"
                autoFocus
                disabled={isWorking}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 border border-theme-border rounded-md focus:outline-none focus:border-theme-info" />
            </div>
            <div>
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={password}
                disabled={isWorking}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border border-theme-border rounded-md focus:outline-none focus:border-theme-info" />
            </div>
            <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isWorking}>
              Login
            </Button>
          </form>
          <div className="min-h-10">
            {failure && (
              <p className="text-theme-danger mt-4 animate-fade-in">
                {failure.message}
                {failure.noAccount && signupUrl && <>{" "}<a href={signupUrl} className="font-semibold underline">Go to the demo sign-up</a></>}
              </p>
            )}
          </div>
          {signupUrl && (
            <p className="text-sm text-center text-theme-text-muted">
              Don&apos;t have an account yet? <a href={signupUrl} className="text-theme-info hover:underline">Sign up for the demo</a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
